import { useEffect, useRef, useState } from "react";
import { View } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Venus, Mars } from "lucide-react-native";
import OptionCard from "../components/OptionCard";
import { useOnboarding } from "../OnboardingContext";
import OnboardingLayout, {
  ScreenTitle,
  ScreenSubtitle,
} from "../components/OnboardingLayout";
import { AUTO_ADVANCE_DELAY } from "../theme";
import { useTranslations } from "@src/packages/i18n";
import type { OnboardingStackParamList, Gender } from "../types";

type Props = NativeStackScreenProps<OnboardingStackParamList, "Sex">;

export default function SexScreen({ navigation }: Props) {
  const t = useTranslations();
  const { data, updateField } = useOnboarding();
  const [gender, setGender] = useState(data.gender);
  const advanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const advancing = useRef(false);

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    [],
  );

  const handleGenderSelect = (value: Gender) => {
    if (advancing.current) return;
    advancing.current = true;
    setGender(value);
    updateField("gender", value);
    if (data.primaryIntent === "donate") {
      updateField(
        "treatmentIntent",
        value === "AFAB" ? "egg-donation" : "sperm-donation",
      );
    }
    advanceTimer.current = setTimeout(() => {
      advancing.current = false;
      advanceTimer.current = null;
      if (data.primaryIntent === "grow") {
        navigation.navigate("JourneyStage", { stage: "grow" });
      } else {
        navigation.navigate("Birthday");
      }
    }, AUTO_ADVANCE_DELAY);
  };

  return (
    <OnboardingLayout bottomPadding={8}>
      <ScreenTitle>{t("onboarding.sex.title")}</ScreenTitle>
      <ScreenSubtitle>{t("onboarding.sex.subtitle")}</ScreenSubtitle>
      <View style={{ flexDirection: "row", gap: 12, width: "100%" }}>
        <OptionCard
          icon={
            <Venus
              size={44}
              color="rgba(255, 235, 222, 0.88)"
              strokeWidth={1.35}
            />
          }
          label={t("onboarding.sex.afab_label")}
          selected={gender === "AFAB"}
          onPress={() => handleGenderSelect("AFAB")}
          layout="tile"
        />
        <OptionCard
          icon={
            <Mars
              size={44}
              color="rgba(255, 235, 222, 0.88)"
              strokeWidth={1.35}
            />
          }
          label={t("onboarding.sex.amab_label")}
          selected={gender === "AMAB"}
          onPress={() => handleGenderSelect("AMAB")}
          layout="tile"
        />
      </View>
    </OnboardingLayout>
  );
}
